import React, { useState, useEffect, useMemo, useCallback } from "react";

import api from "./api";
import {
  weekStartIsoForIndex,
  datesForWeekOffset,
  PLANNING_API_WEEK_INDICES,
  PLANNING_WEEK_INDEX_MIN,
  PLANNING_WEEK_INDEX_MAX,
  relativeCalendarWeekLabel,
} from "./planningCalendar";

const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function apiPlanningErrorMessage(err) {
  const code = err?.data?.error;
  if (err?.status === 429 || code === "rate_limited") return "Trop de requêtes, réessaie dans une minute.";
  if (err?.status === 409 || code === "week_already_validated")
    return "Semaine déjà validée : les heures réelles ne sont plus modifiables.";
  if (err?.status === 403 && code === "week_not_published")
    return "Cette semaine n'est pas encore publiée par ton responsable.";
  if (err?.status === 503 && code === "database_schema_outdated")
    return "Mise à jour serveur requise : la base de données n’a pas encore la table de publication des semaines (contacter l’administrateur).";
  if (code === "invalid_credentials") return "Session expirée, reconnecte-toi.";
  if (code) return `Erreur serveur (${code}).`;
  return err?.message || "Erreur réseau ou serveur.";
}

/** Aligné sur App.js / Prisma : WEEKLY = volume hebdo, HOURLY = payé à l'heure */
const CONTRACT_BASE = { WEEKLY: "WEEKLY", HOURLY: "HOURLY" };

function formatEmployeePlanningSubtitle(employee, employeeContractHours, employeeHourlyRate, employeeContractBase) {
  const base = employeeContractBase[employee] ?? CONTRACT_BASE.WEEKLY;
  const rate = employeeHourlyRate[employee];
  const ch = employeeContractHours[employee];
  const rateNum = Number(rate);
  const rateStr =
    Number.isFinite(rateNum) && rateNum > 0 ? String(rateNum).replace(".", ",") : "—";
  if (base === CONTRACT_BASE.HOURLY) {
    return `Payé à l'heure · ${rateStr} / h`;
  }
  const chNum = Number(ch);
  if (Number.isFinite(chNum) && chNum > 0) {
    return `Contrat ${String(chNum).replace(".", ",")} h / sem. · ${rateStr} / h`;
  }
  return `Taux ${rateStr} / h`;
}

function formatDateDDMM(date) {
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

/** Ex. « Lundi 04/05/2026 » */
function formatWeekdayWithDate(dayName, date) {
  return `${dayName} ${formatDateDDMM(date)}`;
}

// configuration simple des types de jour pour la couleur (par défaut)
// "work" = travail (bleu clair), "off" = repos, "holiday" = jour férié, "vacation" = vacances
const dayTypes = {
  Lundi: "work",
  Mardi: "work",
  Mercredi: "work",
  Jeudi: "work",
  Vendredi: "work",
  Samedi: "work",
  Dimanche: "off",
};

function getShift(shiftsData, employee, day) {
  const s = shiftsData.find((shift) => shift.employee === employee && shift.day === day);
  return s ? s.shift : "-";
}

function splitShift(shiftString) {
  if (!shiftString || shiftString === "-") {
    return { morning: "", afternoon: "" };
  }
  const parts = shiftString.split("/").map((p) => p.trim());
  if (parts.length === 1) {
    return { morning: parts[0], afternoon: "" };
  }
  return { morning: parts[0], afternoon: parts[1] };
}

function getCellStyle(employee, day, employeeDayStatus, overrideType) {
  const type = overrideType ?? employeeDayStatus[employee]?.[day] ?? dayTypes[day] ?? "work";

  if (type === "holiday") {
    return {
      background: "#fecaca",
      color: "#7f1d1d",
    };
  }

  if (type === "vacation") {
    return {
      background: "#f9a8d4",
      color: "#9d174d",
    };
  }

  if (type === "off") {
    return {
      background: "#e5e7eb",
      color: "#4b5563",
    };
  }

  return {
    background: "#bfdbfe",
    color: "#0b1120",
  };
}

function getDayType(employee, day, employeeDayStatus, overrideType) {
  return overrideType ?? employeeDayStatus[employee]?.[day] ?? dayTypes[day] ?? "work";
}

function getActualShift(actualShifts, employee, day) {
  const emp = actualShifts[employee];
  if (!emp) return "";
  return emp[day] || "";
}

/** Copie une semaine source vers une autre en base (manager uniquement). */
async function copyPlanningWeekToApi({
  employees,
  employeeIdByName,
  targetWeekIndex,
  srcShifts,
  srcStatus,
  srcActual,
  singleEmployee,
}) {
  const week = weekStartIsoForIndex(targetWeekIndex);
  const empList = singleEmployee ? [singleEmployee] : employees;
  for (const emp of empList) {
    const uid = employeeIdByName[emp];
    if (!uid) continue;
    for (const day of days) {
      const status = srcStatus?.[emp]?.[day] ?? "work";
      const row = Array.isArray(srcShifts) ? srcShifts.find((s) => s.employee === emp && s.day === day) : null;
      const shiftStr = status === "work" ? row?.shift ?? "-" : "-";
      const { morning, afternoon } = splitShift(shiftStr === "-" ? "" : shiftStr);
      await api.setShift({
        userId: uid,
        week,
        dayFr: day,
        status,
        morning,
        afternoon,
      });
    }
  }
  for (const emp of empList) {
    const uid = employeeIdByName[emp];
    if (!uid) continue;
    const actEmp = srcActual?.[emp];
    if (!actEmp) continue;
    for (const day of days) {
      const act = actEmp[day];
      if (!act) continue;
      const { morning, afternoon } = splitShift(act);
      await api.setActual({ userId: uid, week, dayFr: day, morning, afternoon });
    }
  }
}

function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}

/**
 * Parmi les indices dans `publishedAsc` (tri croissant), retourne celui dont la distance
 * à `targetIndex` est minimale (ex. employé : semaine actuelle = 0, ou la publiée la plus proche).
 * À égalité de distance : préfère l’indice le plus proche de 0 (ancrage « aujourd’hui »),
 * puis la semaine plus dans le futur.
 */
function nearestPublishedWeekIndex(targetIndex, publishedAsc) {
  if (!publishedAsc.length) return targetIndex;
  if (publishedAsc.includes(targetIndex)) return targetIndex;
  let best = publishedAsc[0];
  let bestDist = Math.abs(best - targetIndex);
  for (let i = 1; i < publishedAsc.length; i++) {
    const w = publishedAsc[i];
    const d = Math.abs(w - targetIndex);
    if (d < bestDist) {
      best = w;
      bestDist = d;
      continue;
    }
    if (d !== bestDist) continue;
    const absBest = Math.abs(best);
    const absW = Math.abs(w);
    if (absW < absBest) {
      best = w;
    } else if (absW === absBest && w > best) {
      best = w;
    }
  }
  return best;
}

function Planning({
  user,
  employees,
  employeeIdByName = {},
  employeeContractHours = {},
  employeeHourlyRate = {},
  employeeContractBase = {},
  onOpenAddEmployee,
  onOpenResetEmployeePin,
  onRemoveEmployee,
  showToast,
}) {
  const isManager = user.role === "manager";
  const today = new Date();

  const [shiftsByWeek, setShiftsByWeek] = useState(() => ({}));
  const [employeeDayStatusByWeek, setEmployeeDayStatusByWeek] = useState(() => ({}));
  const [actualShiftsByWeek, setActualShiftsByWeek] = useState(() => ({}));
  const [weeklySubmissionsByWeek, setWeeklySubmissionsByWeek] = useState(() => ({}));
  const [selectedCell, setSelectedCell] = useState(null); // { employee, day, dayIdx }
  const [editorMorning, setEditorMorning] = useState("");
  const [editorAfternoon, setEditorAfternoon] = useState("");
  const [editorStatus, setEditorStatus] = useState("work");
  const [weekIndex, setWeekIndex] = useState(0);
  const [copyTargetEmployee, setCopyTargetEmployee] = useState(""); // "" = toute l'équipe
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [employeeToDisable, setEmployeeToDisable] = useState("");
  /** Lundis (AAAA-MM-JJ) masqués aux employés tant que le manager n'a pas « publié ». */
  const [hiddenWeekStarts, setHiddenWeekStarts] = useState([]);

  const employeeIdsKey = useMemo(
    () =>
      Object.entries(employeeIdByName)
        .map(([k, v]) => `${k}:${v}`)
        .sort()
        .join("|"),
    [employeeIdByName]
  );

  const refreshWeekFromApi = useCallback(
    async (idx) => {
      const week = weekStartIsoForIndex(idx);
      const data = await api.getPlanning(week);
      setShiftsByWeek((prev) => ({ ...prev, [idx]: data.shifts }));
      setEmployeeDayStatusByWeek((prev) => ({ ...prev, [idx]: data.employeeDayStatus }));
      setActualShiftsByWeek((prev) => ({ ...prev, [idx]: data.actualShifts }));
      setWeeklySubmissionsByWeek((prev) => ({ ...prev, [idx]: data.weeklySubmissions }));
    },
    []
  );

  const refreshHiddenWeeksFromApi = useCallback(async () => {
    const list = await api.getPlanningHiddenWeeks();
    setHiddenWeekStarts(Array.isArray(list) ? list : []);
  }, []);

  const publishedWeekIndicesSorted = useMemo(() => {
    const hidden = new Set(hiddenWeekStarts);
    return PLANNING_API_WEEK_INDICES.filter((idx) => !hidden.has(weekStartIsoForIndex(idx)));
  }, [hiddenWeekStarts]);

  const employeeCanGoPrev =
    isManager || publishedWeekIndicesSorted.some((w) => w < weekIndex);
  const employeeCanGoNext =
    isManager || publishedWeekIndicesSorted.some((w) => w > weekIndex);

  const visibleEmployees = isManager
    ? employees
    : employees.filter((emp) => emp === user.name);

  // Données de la semaine courante (avec repli sur la semaine 0)
  const shifts = shiftsByWeek[weekIndex] ?? shiftsByWeek[0] ?? [];
  const employeeDayStatus =
    employeeDayStatusByWeek[weekIndex] ?? employeeDayStatusByWeek[0] ?? {};
  const actualShifts =
    actualShiftsByWeek[weekIndex] ?? actualShiftsByWeek[0] ?? {};
  const weeklySubmissions =
    weeklySubmissionsByWeek[weekIndex] ?? weeklySubmissionsByWeek[0] ?? {};

  const currentWeekDates = useMemo(() => datesForWeekOffset(weekIndex), [weekIndex]);
  const currentWeekIso = useMemo(() => weekStartIsoForIndex(weekIndex), [weekIndex]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshHiddenWeeksFromApi();
        if (cancelled) return;
        await refreshWeekFromApi(weekIndex);
      } catch (err) {
        if (!cancelled) showToast?.(apiPlanningErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekIndex, user?.id, employeeIdsKey, refreshWeekFromApi, refreshHiddenWeeksFromApi, showToast]);

  useEffect(() => {
    if (isManager) return;
    if (publishedWeekIndicesSorted.length === 0) return;
    if (!publishedWeekIndicesSorted.includes(weekIndex)) {
      setWeekIndex(nearestPublishedWeekIndex(weekIndex, publishedWeekIndicesSorted));
    }
  }, [isManager, weekIndex, publishedWeekIndicesSorted]);

  useEffect(() => {
    if (!disableModalOpen) return;
    setEmployeeToDisable("");
  }, [disableModalOpen]);

  return (
    <div className="page-content">
      <h2 style={{ marginTop: 0, fontSize: "clamp(1.25rem, 4vw, 1.6rem)", color: "#0f172a" }}>Planning hebdomadaire</h2>
      <p style={{ marginBottom: "20px", color: "#555", fontSize: "clamp(0.85rem, 2vw, 1rem)" }}>
        {isManager
          ? "Vue complète de tous les horaires de l'équipe."
          : `Bienvenue ${user.name}, bon courage pour ta semaine !`}
      </p>
      {isManager && (
        <div
          className="legend-mobile"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "12px",
            fontSize: "0.8rem",
            color: "#475569",
          }}
        >
          <span>Légende (clique sur une case pour changer) :</span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "16px",
                height: "10px",
                borderRadius: "4px",
                background: "#bfdbfe",
                border: "1px solid #60a5fa",
              }}
            ></span>
            <span>Travail</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "16px",
                height: "10px",
                borderRadius: "4px",
                background: "#e5e7eb",
                border: "1px solid #9ca3af",
              }}
            ></span>
            <span>Repos</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "16px",
                height: "10px",
                borderRadius: "4px",
                background: "#fecaca",
                border: "1px solid #fca5a5",
              }}
            ></span>
            <span>Jour férié</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "16px",
                height: "10px",
                borderRadius: "4px",
                background: "#f9a8d4",
                border: "1px solid #f472b6",
              }}
            ></span>
            <span>Vacances / congés</span>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          fontSize: "0.85rem",
          color: "#0f172a",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div>
            {(() => {
              const start = currentWeekDates[0];
              const end = currentWeekDates[currentWeekDates.length - 1];
              return (
                <>
                  {formatDateDDMM(start)} – {formatDateDDMM(end)} · {relativeCalendarWeekLabel(weekIndex)}
                </>
              );
            })()}
          </div>
          {isManager ? (
            <button
              type="button"
              title={
                hiddenWeekStarts.includes(currentWeekIso)
                  ? "Brouillon : invisible pour les employés. Cliquer pour publier cette semaine."
                  : "Semaine publiée : visible par les employés. Cliquer pour repasser en brouillon."
              }
              aria-pressed={!hiddenWeekStarts.includes(currentWeekIso)}
              onClick={async () => {
                const iso = currentWeekIso;
                const wasPublished = !hiddenWeekStarts.includes(iso);
                try {
                  await api.setPlanningWeekPublished(iso, !wasPublished);
                  await refreshHiddenWeeksFromApi();
                  showToast?.(
                    wasPublished
                      ? "Semaine repassée en brouillon (les employés ne la voient plus)."
                      : "Semaine publiée : les employés peuvent la consulter."
                  );
                } catch (err) {
                  showToast?.(apiPlanningErrorMessage(err));
                }
              }}
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "12px",
                border: hiddenWeekStarts.includes(currentWeekIso)
                  ? "2px solid #cbd5e1"
                  : "2px solid #16a34a",
                background: hiddenWeekStarts.includes(currentWeekIso)
                  ? "#f8fafc"
                  : "#dcfce7",
                color: hiddenWeekStarts.includes(currentWeekIso)
                  ? "#64748b"
                  : "#15803d",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                padding: 0,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M7.5 12l2.5 2.5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <button
            type="button"
            className="touch-target btn-mobile"
            onClick={() => {
              if (isManager) setWeekIndex((prev) => Math.max(PLANNING_WEEK_INDEX_MIN, prev - 1));
              else {
                const prevPublished = [...publishedWeekIndicesSorted]
                  .reverse()
                  .find((w) => w < weekIndex);
                if (prevPublished !== undefined) setWeekIndex(prevPublished);
              }
            }}
            disabled={!isManager && !employeeCanGoPrev}
            style={{
              borderRadius: "999px",
              border: "1px solid #cbd5f5",
              background: "white",
              padding: "8px 12px",
              fontSize: "0.85rem",
              cursor: !isManager && !employeeCanGoPrev ? "not-allowed" : "pointer",
            }}
          >
            ◀ Semaine précédente
          </button>
          <button
            type="button"
            className="touch-target btn-mobile"
            onClick={() => {
              if (isManager) setWeekIndex((prev) => Math.min(PLANNING_WEEK_INDEX_MAX, prev + 1));
              else {
                const nextPublished = publishedWeekIndicesSorted.find((w) => w > weekIndex);
                if (nextPublished !== undefined) setWeekIndex(nextPublished);
              }
            }}
            disabled={!isManager && !employeeCanGoNext}
            style={{
              borderRadius: "999px",
              border: "1px solid #cbd5f5",
              background: "white",
              padding: "8px 12px",
              fontSize: "0.85rem",
              cursor: !isManager && !employeeCanGoNext ? "not-allowed" : "pointer",
            }}
          >
            Semaine suivante ▶
          </button>
          <button
            type="button"
            className="touch-target btn-mobile"
            onClick={() => setWeekIndex(0)}
            style={{
              borderRadius: "999px",
              border: "1px solid #0f4dcc",
              background: "white",
              color: "#0f4dcc",
              padding: "8px 12px",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            Semaine actuelle
          </button>
        </div>
      </div>

      {isManager && (
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            fontSize: "0.85rem",
          }}
        >
          <span style={{ fontWeight: 600 }}>Gestion des employés :</span>
          <button
            type="button"
            onClick={() => setDisableModalOpen(true)}
            disabled={employees.length === 0}
            style={{
              padding: "4px 10px",
              borderRadius: "999px",
              border: "1px solid #cbd5f5",
              fontSize: "0.8rem",
              background: "white",
              cursor: employees.length === 0 ? "not-allowed" : "pointer",
              opacity: employees.length === 0 ? 0.55 : 1,
            }}
          >
            Désactiver un employé…
          </button>
          <button
            type="button"
            onClick={() => onOpenAddEmployee && onOpenAddEmployee()}
            style={{
              borderRadius: "999px",
              border: "1px solid #0f4dcc",
              background: "white",
              color: "#0f4dcc",
              padding: "4px 10px",
              fontSize: "0.8rem",
              cursor: "pointer",
            }}
          >
            + Ajouter un employé
          </button>
          {onOpenResetEmployeePin ? (
            <button
              type="button"
              onClick={() => onOpenResetEmployeePin()}
              disabled={employees.length === 0}
              style={{
                borderRadius: "999px",
                border: "1px solid #7c3aed",
                background: "white",
                color: "#7c3aed",
                padding: "4px 10px",
                fontSize: "0.8rem",
                cursor: employees.length === 0 ? "not-allowed" : "pointer",
                opacity: employees.length === 0 ? 0.55 : 1,
              }}
            >
              Réinitialiser code employé…
            </button>
          ) : null}

          <div
            style={{
              fontSize: "0.78rem",
              color: "#64748b",
              maxWidth: "640px",
              lineHeight: 1.45,
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "10px 12px",
              background: "#f8fafc",
            }}
          >
            <strong>Publication :</strong> l&apos;icône à côté des dates (coche verte) indique que la semaine
            est visible par les employés ; en gris, elle reste en brouillon. Le reste du planning est enregistré
            sur le serveur. Pour un jour férié ou un congé, ouvre une case et choisis le statut approprié.
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
            <span style={{ fontWeight: 600 }}>Copier cette semaine sur la suivante :</span>
            <select
              value={copyTargetEmployee}
              onChange={(e) => setCopyTargetEmployee(e.target.value)}
              style={{
                padding: "4px 8px",
                borderRadius: "999px",
                border: "1px solid #cbd5f5",
                fontSize: "0.8rem",
              }}
            >
              <option value="">Toute l'équipe</option>
              {employees.map((emp) => (
                <option key={emp} value={emp}>
                  {emp}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={async () => {
                const targetWeek = Math.min(PLANNING_WEEK_INDEX_MAX, weekIndex + 1);
                const srcShifts = shiftsByWeek[weekIndex] ?? shiftsByWeek[0] ?? [];
                const srcStatus =
                  employeeDayStatusByWeek[weekIndex] ?? employeeDayStatusByWeek[0] ?? {};
                const srcActual = actualShiftsByWeek[weekIndex] ?? actualShiftsByWeek[0] ?? {};
                try {
                  await copyPlanningWeekToApi({
                    employees,
                    employeeIdByName,
                    targetWeekIndex: targetWeek,
                    srcShifts,
                    srcStatus,
                    srcActual,
                    singleEmployee: copyTargetEmployee || null,
                  });
                  await refreshWeekFromApi(targetWeek);
                  setWeekIndex(targetWeek);
                  showToast && showToast("Semaine copiée sur le serveur.");
                } catch (err) {
                  showToast?.(apiPlanningErrorMessage(err));
                }
              }}
              style={{
                borderRadius: "999px",
                border: "1px solid #0f4dcc",
                background: "#0f4dcc",
                color: "white",
                padding: "4px 10px",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              Copier puis aller à la semaine suivante
            </button>
          </div>
        </div>
      )}

      {isManager && (
        <div
          style={{
            marginBottom: "16px",
            fontSize: "0.85rem",
            color: "#0f172a",
          }}
        >
          {employees.filter((e) => weeklySubmissions[e] === "employee_submitted").length > 0 ? (
            <>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                Semaines à valider :
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {employees
                  .filter((e) => weeklySubmissions[e] === "employee_submitted")
                  .map((e) => (
                    <div
                      key={e}
                      style={{
                        background: "white",
                        borderRadius: "999px",
                        border: "1px solid #cbd5f5",
                        padding: "4px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span>{e}</span>
                      <button
                        type="button"
                        onClick={async () => {
                          const uid = employeeIdByName[e];
                          if (!uid) {
                            showToast?.("Employé introuvable.");
                            return;
                          }
                          try {
                            await api.validateWeek({
                              userId: uid,
                              week: weekStartIsoForIndex(weekIndex),
                              status: "validated",
                            });
                            await refreshWeekFromApi(weekIndex);
                            showToast && showToast(`Semaine validée pour ${e}.`);
                          } catch (err) {
                            showToast?.(apiPlanningErrorMessage(err));
                          }
                        }}
                        style={{
                          border: "none",
                          background: "#16a34a",
                          color: "white",
                          borderRadius: "999px",
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                        }}
                      >
                        Valider
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const uid = employeeIdByName[e];
                          if (!uid) {
                            showToast?.("Employé introuvable.");
                            return;
                          }
                          try {
                            await api.validateWeek({
                              userId: uid,
                              week: weekStartIsoForIndex(weekIndex),
                              status: "rejected",
                            });
                            await refreshWeekFromApi(weekIndex);
                            showToast && showToast(`Semaine refusée pour ${e}.`);
                          } catch (err) {
                            showToast?.(apiPlanningErrorMessage(err));
                          }
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#b91c1c",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                        }}
                      >
                        Refuser
                      </button>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              Aucune semaine en attente de validation.
            </div>
          )}
        </div>
      )}

      {!isManager && publishedWeekIndicesSorted.length === 0 ? (
        <div
          style={{
            marginTop: "8px",
            padding: "20px 16px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            color: "#475569",
            fontSize: "0.9rem",
            lineHeight: 1.5,
          }}
        >
          Aucune semaine n&apos;est publiée pour l&apos;instant. Ton responsable doit rendre au moins une
          semaine visible (icône verte à côté des dates) pour que tu puisses consulter le planning ici.
        </div>
      ) : (
        <>
      <div className="planning-table-wrap">
        <table
          style={{
            borderCollapse: "collapse",
            minWidth: "900px",
            background: "white",
            boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <thead style={{ background: "#0f172a", color: "white" }}>
            <tr>
              <th
                rowSpan={2}
                className="sticky-col-employe"
                style={{ padding: "12px 16px", textAlign: "left", verticalAlign: "middle", background: "#0f172a" }}
              >
                Employé
              </th>
              {days.map((day, idx) => {
                const isTodayCol = isSameDay(currentWeekDates[idx], today);
                return (
                <th
                  key={day}
                  colSpan={2}
                  style={{
                    padding: "8px 12px",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    ...(isTodayCol ? { background: "rgba(255,255,255,0.2)", boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.5)" } : {}),
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                    }}
                  >
                    {formatWeekdayWithDate(day, currentWeekDates[idx])}
                  </span>
                </th>
              ); })}
            </tr>
            <tr>
              {days.map((day, idx) => {
                const isTodayCol = isSameDay(currentWeekDates[idx], today);
                const thStyle = {
                  padding: "4px 8px",
                  textAlign: "center",
                  fontSize: "0.8rem",
                  ...(isTodayCol ? { background: "rgba(255,255,255,0.2)" } : {}),
                };
                return (
                <React.Fragment key={`${day}-sub`}>
                  <th style={thStyle}>Matin</th>
                  <th style={thStyle}>Après-midi</th>
                </React.Fragment>
              ); })}
            </tr>
          </thead>
          <tbody>
            {visibleEmployees.map((employee) => {
              return (
                <tr key={employee}>
                  <td
                    className="sticky-col-employe"
                    style={{
                      padding: "10px 14px",
                      fontWeight: 600,
                      background: "#f1f5f9",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <div>{employee}</div>
                    <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "4px" }}>
                      {formatEmployeePlanningSubtitle(
                        employee,
                        employeeContractHours,
                        employeeHourlyRate,
                        employeeContractBase
                      )}
                    </div>
                  </td>
                  {days.map((day, dayIdx) => {
                    const baseStyle = {
                      padding: "8px 10px",
                      textAlign: "center",
                      borderBottom: "1px solid #e2e8f0",
                      fontSize: "0.8rem",
                    };
                    const isTodayCol = isSameDay(currentWeekDates[dayIdx], today);
                    const todayCellStyle = isTodayCol ? { boxShadow: "inset 0 0 0 2px #0f4dcc" } : {};
                    const dayType = getDayType(employee, day, employeeDayStatus);
                    const colorStyle = getCellStyle(employee, day, employeeDayStatus, dayType);

                    const shift = getShift(shifts, employee, day);
                    const { morning, afternoon } = splitShift(shift);
                    const actual = getActualShift(actualShifts, employee, day);
                    const { morning: actualMorning, afternoon: actualAfternoon } = splitShift(actual);

                    const handleOpenEditor = () => {
                      const plannedShift = getShift(shifts, employee, day) || "";
                      const { morning: mPlanned, afternoon: aPlanned } = splitShift(plannedShift);
                      const actualShift = getActualShift(actualShifts, employee, day) || "";
                      const { morning: mActual, afternoon: aActual } = splitShift(actualShift);
                      setSelectedCell({ employee, day, dayIdx });
                      setEditorStatus(dayType);
                      if (isManager) {
                        setEditorMorning(dayType === "work" ? mPlanned : "");
                        setEditorAfternoon(dayType === "work" ? aPlanned : "");
                      } else {
                        setEditorMorning(mActual);
                        setEditorAfternoon(aActual);
                      }
                    };

                    let label = "";
                    if (dayType === "off") label = "Repos";
                    else if (dayType === "vacation") label = "Vacances";
                    else if (dayType === "holiday") label = "Férié";

                    const displayMorning =
                      dayType !== "work"
                        ? label || "-"
                        : isManager
                        ? morning || "-"
                        : actualMorning || morning || "-";
                    const displayAfternoon =
                      dayType !== "work"
                        ? label || "-"
                        : isManager
                        ? afternoon || "-"
                        : actualAfternoon || afternoon || "-";

                    return (
                      <React.Fragment key={day}>
                        <td
                          style={{
                            ...baseStyle,
                            ...colorStyle,
                            ...todayCellStyle,
                            cursor: "pointer",
                            borderRight: "1px solid #e2e8f0",
                          }}
                          onClick={handleOpenEditor}
                        >
                          {displayMorning}
                        </td>
                        <td
                          style={{
                            ...baseStyle,
                            ...colorStyle,
                            ...todayCellStyle,
                            cursor: "pointer",
                          }}
                          onClick={handleOpenEditor}
                        >
                          {displayAfternoon}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isManager && (
        <div style={{ marginTop: "16px" }}>
          <button
            type="button"
            className="touch-target btn-mobile"
            onClick={async () => {
              try {
                await api.submitWeek(weekStartIsoForIndex(weekIndex));
                await refreshWeekFromApi(weekIndex);
                showToast?.("Semaine transmise au manager.");
              } catch (err) {
                showToast?.(apiPlanningErrorMessage(err));
              }
            }}
            style={{
              background: "#0f4dcc",
              color: "white",
              border: "none",
              borderRadius: "999px",
              padding: "12px 20px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            Valider ma semaine
          </button>
          {weeklySubmissions[user.name] === "employee_submitted" && (
            <span
              style={{
                marginLeft: "10px",
                fontSize: "0.8rem",
                color: "#22c55e",
              }}
            >
              En attente de validation du manager
            </span>
          )}
          {weeklySubmissions[user.name] === "manager_validated" && (
            <span
              style={{
                marginLeft: "10px",
                fontSize: "0.8rem",
                color: "#16a34a",
              }}
            >
              Semaine validée par le manager
            </span>
          )}
        </div>
      )}
        </>
      )}
      {isManager && disableModalOpen && (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1150,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={() => setDisableModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="disable-employee-title"
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "420px",
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 18px 35px rgba(15,23,42,0.18)",
              border: "1px solid #e2e8f0",
              padding: "18px 20px",
            }}
          >
            <h3 id="disable-employee-title" style={{ margin: "0 0 8px", fontSize: "1.05rem", color: "#0f172a" }}>
              Désactiver un employé
            </h3>
            <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "#64748b", lineHeight: 1.45 }}>
              L&apos;employé disparaît du planning et ne pourra plus se connecter avec son nom tant qu&apos;il
              n&apos;est pas rajouté.
            </p>
            <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a", display: "block" }}>
              Employé à retirer
              <select
                value={employeeToDisable}
                onChange={(e) => setEmployeeToDisable(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "6px",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  border: "1px solid #cbd5f5",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                }}
              >
                <option value="">Choisir…</option>
                {employees.map((emp) => (
                  <option key={emp} value={emp}>
                    {emp}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "18px" }}>
              <button
                type="button"
                onClick={() => setDisableModalOpen(false)}
                style={{
                  borderRadius: "999px",
                  border: "1px solid #cbd5f5",
                  background: "white",
                  color: "#475569",
                  padding: "8px 16px",
                  fontSize: "0.9rem",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!employeeToDisable) return;
                  try {
                    if (onRemoveEmployee) await onRemoveEmployee(employeeToDisable);
                  } finally {
                    setDisableModalOpen(false);
                  }
                }}
                disabled={!employeeToDisable}
                style={{
                  borderRadius: "999px",
                  border: "none",
                  background: employeeToDisable ? "#b91c1c" : "#e2e8f0",
                  color: employeeToDisable ? "white" : "#94a3b8",
                  padding: "8px 18px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  cursor: employeeToDisable ? "pointer" : "not-allowed",
                }}
              >
                Désactiver
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedCell && (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={() => setSelectedCell(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="planning-editor-title"
            className="editor-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "420px",
              width: "100%",
              maxHeight: "min(90vh, 640px)",
              overflowY: "auto",
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 18px 35px rgba(15,23,42,0.18)",
              border: "1px solid #e2e8f0",
              padding: "16px 18px",
            }}
          >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div>
              <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Édition du créneau</div>
              <div id="planning-editor-title" style={{ fontWeight: 600 }}>
                {selectedCell.employee} –{" "}
                {selectedCell.dayIdx !== undefined && currentWeekDates[selectedCell.dayIdx]
                  ? formatWeekdayWithDate(selectedCell.day, currentWeekDates[selectedCell.dayIdx])
                  : selectedCell.day}
              </div>
            </div>
            <button
              type="button"
              className="touch-target"
              onClick={() => setSelectedCell(null)}
              style={{
                border: "none",
                background: "transparent",
                color: "#64748b",
                cursor: "pointer",
                fontSize: "0.9rem",
                padding: "8px",
              }}
            >
              Fermer
            </button>
          </div>

          {isManager ? (
            <>
              <div style={{ marginBottom: "10px" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 500, color: "#0f172a" }}>
                  Statut du jour
                  <select
                    value={editorStatus}
                    onChange={(e) => setEditorStatus(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "4px",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      border: "1px solid #cbd5f5",
                      fontSize: "0.85rem",
                    }}
                  >
                    <option value="work">Travail</option>
                    <option value="off">Repos</option>
                    <option value="holiday">Jour férié</option>
                    <option value="vacation">Vacances / congés</option>
                  </select>
                </label>
              </div>

              {editorStatus === "work" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                    Matin
                    <input
                      type="text"
                      value={editorMorning}
                      onChange={(e) => setEditorMorning(e.target.value)}
                      placeholder="ex: 08:00 - 12:00"
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        padding: "6px 10px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5f5",
                        fontSize: "0.85rem",
                      }}
                    />
                  </label>
                  <label style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                    Après-midi
                    <input
                      type="text"
                      value={editorAfternoon}
                      onChange={(e) => setEditorAfternoon(e.target.value)}
                      placeholder="ex: 18:00 - 22:00"
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        padding: "6px 10px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5f5",
                        fontSize: "0.85rem",
                      }}
                    />
                  </label>
                </div>
              )}

              <button
                type="button"
                onClick={async () => {
                  const { employee, day } = selectedCell;

                  const uid = employeeIdByName[employee];
                  if (!uid) {
                    showToast?.("Employé introuvable : recharge la page après connexion.");
                    return;
                  }
                  try {
                    await api.setShift({
                      userId: uid,
                      week: weekStartIsoForIndex(weekIndex),
                      dayFr: day,
                      status: editorStatus,
                      morning: editorMorning.trim(),
                      afternoon: editorAfternoon.trim(),
                    });
                    await refreshWeekFromApi(weekIndex);
                    setSelectedCell(null);
                  } catch (err) {
                    showToast?.(apiPlanningErrorMessage(err));
                  }
                }}
                style={{
                  marginTop: "6px",
                  width: "100%",
                  background: "#0f4dcc",
                  color: "white",
                  border: "none",
                  borderRadius: "999px",
                  padding: "8px 0",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                Enregistrer
              </button>
            </>
          ) : (
            (() => {
              const { employee, day } = selectedCell;
              const type = getDayType(employee, day, employeeDayStatus);
              const planned = getShift(shifts, employee, day);
              const { morning: plannedMorning, afternoon: plannedAfternoon } = splitShift(planned);

              let label = "Travail";
              if (type === "off") label = "Repos";
              else if (type === "vacation") label = "Vacances / congés";
              else if (type === "holiday") label = "Jour férié";

              return (
                <div style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                  <p style={{ margin: "0 0 6px" }}>
                    <strong>Statut :</strong> {label}
                  </p>
                  {type === "work" && (
                    <>
                      <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: "#64748b" }}>
                        <strong>Heures prévues :</strong>
                      </p>
                      <p style={{ margin: "0 0 2px", fontSize: "0.85rem" }}>
                        Matin : {plannedMorning || "-"}
                      </p>
                      <p style={{ margin: "0 0 6px", fontSize: "0.85rem" }}>
                        Après-midi : {plannedAfternoon || "-"}
                      </p>

                      <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: "#64748b" }}>
                        <strong>Heures réelles (à saisir) :</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setEditorMorning(plannedMorning || "");
                          setEditorAfternoon(plannedAfternoon || "");
                        }}
                        style={{
                          marginBottom: "6px",
                          borderRadius: "999px",
                          border: "1px solid #0f4dcc",
                          background: "white",
                          color: "#0f4dcc",
                          padding: "4px 10px",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        Copier les heures prévues
                      </button>
                      <label style={{ fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>
                        Matin
                        <input
                          type="text"
                          value={editorMorning}
                          onChange={(e) => setEditorMorning(e.target.value)}
                          placeholder={plannedMorning || "08:00 - 12:00"}
                          style={{
                            width: "100%",
                            marginTop: "2px",
                            padding: "4px 8px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5f5",
                            fontSize: "0.8rem",
                          }}
                        />
                      </label>
                      <label style={{ fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>
                        Après-midi
                        <input
                          type="text"
                          value={editorAfternoon}
                          onChange={(e) => setEditorAfternoon(e.target.value)}
                          placeholder={plannedAfternoon || "18:00 - 22:00"}
                          style={{
                            width: "100%",
                            marginTop: "2px",
                            padding: "4px 8px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5f5",
                            fontSize: "0.8rem",
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.setActual({
                              userId: user.id,
                              week: weekStartIsoForIndex(weekIndex),
                              dayFr: day,
                              morning: editorMorning.trim(),
                              afternoon: editorAfternoon.trim(),
                            });
                            await refreshWeekFromApi(weekIndex);
                            setSelectedCell(null);
                          } catch (err) {
                            showToast?.(apiPlanningErrorMessage(err));
                          }
                        }}
                        style={{
                          marginTop: "4px",
                          width: "100%",
                          background: "#0f4dcc",
                          color: "white",
                          border: "none",
                          borderRadius: "999px",
                          padding: "6px 0",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                        }}
                      >
                        Enregistrer mes heures
                      </button>
                    </>
                  )}
                  {type !== "work" && (
                    <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
                      Pas d'heures ce jour-là.
                    </p>
                  )}
                </div>
              );
            })()
          )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Planning;
